import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import LocalGroceryStoreIcon from '@mui/icons-material/LocalGroceryStore';
import {
	ListItemText,
	TableHead,
	TableRow,
	TableCell,
	Grid,
	Tooltip,
	IconButton,
	LinearProgress,
	CircularProgress,
	Box,
	Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';

import SearchToolbar from 'components/SearchToolbar';
import ResponsiveTable from 'components/ResponsiveTable';
import { CustomTooltipProps } from 'components/tooltips/CustomTooltip/CustomTooltip';
import DateFilters from 'components/SoldProductsFilters/DateFilters';
import OtherFilters from 'components/SoldProductsFilters/OtherFilters';
import moment from 'moment';
import { price, formatNumber } from 'lib/numbers';

import useListSoldProducts from 'hooks/useListSoldProducts';
import { FetchSoldProductsSearchProps, ListSoldProducts } from 'lib/models/listSoldProducts';
import { OrderStatus } from 'lib/models/order';
import useSoldSummary from 'hooks/useSoldSummary';
import SoldSummaryBlock, { MuteCardChip } from 'components/SoldSummary';
import { SoldProductsFiltersState, FilterAction } from 'hooks/useSoldProductsFilters';
import { defaultSoldProductsRowsPerPage, soldProductsRowsPerPageOptions } from 'config/constants';

type Props = {
	editUrl?: string;
	paper?: boolean;
	disabled?: boolean;
	refetch?: () => {};
	customAction?: CustomTooltipProps;
	filters: SoldProductsFiltersState;
	dispatch: React.Dispatch<FilterAction>;
};

const getStatusLabel = (status: string): string => {
	switch (status) {
		case OrderStatus.closed:
			return 'Entregado';
		case OrderStatus.active:
			return 'Pagado';
		case OrderStatus.cancelled:
			return 'Devuelto';
		default:
			return status;
	}
};

export default function ListSoldProductsTable(props: Props) {
	const { filters, dispatch } = props;
	const {
		from,
		to,
		confirmationFrom,
		confirmationTo,
		cancellationFrom,
		cancellationTo,
		reservationFrom,
		reservationTo,
		selectedStatuses,
		selectedVendors,
		selectedSections,
		isInAppSale,
		inAppSaleType,
		reservationType,
	} = filters;

	const { t } = useTranslation();

	const [search, setSearch] = useState('');
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(defaultSoldProductsRowsPerPage);

	// Build filter props shared across list/summary
	const filterProps: FetchSoldProductsSearchProps = useMemo(() => {
		const props: FetchSoldProductsSearchProps = {};

		if (search) {
			props.search = search;
		}
		if (from) {
			props.from = from.toISOString();
		}
		if (to) {
			props.to = to.toISOString();
		}
		if (confirmationFrom) {
			props.confirmationFrom = confirmationFrom.toISOString();
		}
		if (confirmationTo) {
			props.confirmationTo = confirmationTo.toISOString();
		}
		if (cancellationFrom) {
			props.cancellationFrom = cancellationFrom.toISOString();
		}
		if (cancellationTo) {
			props.cancellationTo = cancellationTo.toISOString();
		}
		if (reservationFrom) {
			props.reservationFrom = reservationFrom.toISOString();
		}
		if (reservationTo) {
			props.reservationTo = reservationTo.toISOString();
		}
		if (selectedStatuses.length > 0) {
			props.statuses = selectedStatuses;
		}
		if (selectedVendors.length > 0) {
			props.vendors = selectedVendors;
		}
		if (selectedSections.length > 0) {
			props.sections = selectedSections;
		}
		if (isInAppSale !== null) {
			props.isInAppSale = isInAppSale;
		}
		if (inAppSaleType) {
			props.inAppSaleType = inAppSaleType;
		}
		if (reservationType) {
			props.reservationType = reservationType;
		}

		return props;
	}, [
		search,
		from,
		to,
		confirmationFrom,
		confirmationTo,
		cancellationFrom,
		cancellationTo,
		reservationFrom,
		reservationTo,
		selectedStatuses,
		selectedVendors,
		selectedSections,
		isInAppSale,
		inAppSaleType,
		reservationType,
	]);

	const searchProps: FetchSoldProductsSearchProps = useMemo(
		() => ({
			...filterProps,
			limit: rowsPerPage,
			skip: page * rowsPerPage,
			sort: { createdAt: 'desc' },
		}),
		[filterProps, rowsPerPage, page]
	);

	const { loading, soldProducts, refetch } = useListSoldProducts(searchProps);
	const { loading: summaryLoading, summary } = useSoldSummary(filterProps);

	const handleChangePage = (_: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const displayedProducts = soldProducts?.elements ?? [];
	const totalCount = soldProducts?.total ?? 0;

	const [exportingCsv, setExportingCsv] = useState(false);

	const exportToCsv = async () => {
		try {
			setExportingCsv(true);

			// Fetch all results for export
			// Use a high limit to ensure we get all results
			const exportProps: FetchSoldProductsSearchProps = {
				sort: { createdAt: 'desc' },
			};

			if (search) {
				exportProps.search = search;
			}
			if (from) {
				exportProps.from = from.toISOString();
			}
			if (to) {
				exportProps.to = to.toISOString();
			}
			if (confirmationFrom) {
				exportProps.confirmationFrom = confirmationFrom.toISOString();
			}
			if (confirmationTo) {
				exportProps.confirmationTo = confirmationTo.toISOString();
			}
			if (cancellationFrom) {
				exportProps.cancellationFrom = cancellationFrom.toISOString();
			}
			if (cancellationTo) {
				exportProps.cancellationTo = cancellationTo.toISOString();
			}
			if (reservationFrom) {
				exportProps.reservationFrom = reservationFrom.toISOString();
			}
			if (reservationTo) {
				exportProps.reservationTo = reservationTo.toISOString();
			}
			if (selectedStatuses.length > 0) {
				exportProps.statuses = selectedStatuses;
			}
			if (selectedVendors.length > 0) {
				exportProps.vendors = selectedVendors;
			}
			if (selectedSections.length > 0) {
				exportProps.sections = selectedSections;
			}
			if (inAppSaleType) {
				exportProps.inAppSaleType = inAppSaleType;
			}
			if (reservationType) {
				exportProps.reservationType = reservationType;
			}

			const allProducts = await ListSoldProducts(exportProps);
			const productsToExport = allProducts?.elements ?? [];

			const headers = [
				'Fecha Creación',
				'Fecha Confirmación',
				'Fecha Cancelación',
				...(inAppSaleType === 'reservation' ? ['Fecha Reserva'] : []),
				'Código Producto',
				'Categoría',
				'Descripción',
				'Cantidad',
				'Precio',
				'Monto Pagado',
				'Comprador',
				'Usuario interno',
				'Código Orden',
				'Estado',
				'Total orden',
				'C/Servicio Orden',
				'Descuento Orden',
				// 'Total Pagado',
				'Puntos Usados',
				'Método de Pago',
				'Locación',
				t('nfc:muteCard'),
			];

			const rows = productsToExport.map((product) => {
				// Format using the browser's local timezone to match on-screen values
				const createdDate = moment(product.createdAt).format('YYYY-MM-DD HH:mm');
				const confirmationDate = product.confirmationDate
					? moment(product.confirmationDate).format('YYYY-MM-DD HH:mm')
					: '-';
				const cancellationDate = product.cancellationDate
					? moment(product.cancellationDate).format('YYYY-MM-DD HH:mm')
					: '-';
				const reservationDate =
					inAppSaleType === 'reservation' && product.reservationDate
						? moment(product.reservationDate).format('YYYY-MM-DD HH:mm')
						: '-';

				const getSeller = () => {
					if (product.status === 'closed' && product.confirmedBy) {
						return product.confirmedBy.username;
					}
					if (product.status === 'cancelled' && product.cancelledBy) {
						return product.cancelledBy.username;
					}
					if (!product.isInAppSale && product.orderBy) {
						return product.orderBy.username;
					}
					return '-';
				};

				const row = [
					createdDate,
					confirmationDate,
					cancellationDate,
					...(inAppSaleType === 'reservation' ? [reservationDate] : []),
					product.code || '-',
					product.categories?.length ? product.categories.join(', ') : '-',
					product.title,
					product.quantity,
					formatNumber(product.price ?? 0),
					product.productAmountPaid !== null && product.productAmountPaid !== undefined
						? formatNumber(product.productAmountPaid)
						: 'N/A',
					product.orderTo?.username || product.vendor || '-',
					getSeller(),
					product.orderCode || 'unknown',
					getStatusLabel(product.status),
					formatNumber(product.orderTotal ?? 0),
					formatNumber(product.saleFee ?? 0),
					formatNumber(product.saleDiscount ?? 0),
					// formatNumber(product.amountPaid ?? product.priceTotal ?? 0),
					product.pointsUsed ?? 0,
					getPaymentMethod(product.paymentMethod || '-'),
					product?.sectionData?.title || '-',
					product.isNfcCard ? t('nfc:muteCardYes') : t('nfc:muteCardNo'),
				];
				return row;
			});

			let csvContent = 'data:text/csv;charset=utf-8,';
			csvContent += headers.join(',') + '\n';
			rows.forEach((row) => {
				csvContent += row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(',') + '\n';
			});

			const encodedUri = encodeURI(csvContent);
			const timestamp = moment().format('YYYY-MM-DD_HH-mm');
			const link = document.createElement('a');
			link.setAttribute('href', encodedUri);
			link.setAttribute('download', `lista_productos_vendidos_${timestamp}.csv`);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (error) {
			console.error('Error exporting CSV:', error);
		} finally {
			setExportingCsv(false);
		}
	};

	useEffect(() => {
		setPage(0);
	}, [
		search,
		from,
		to,
		confirmationFrom,
		confirmationTo,
		cancellationFrom,
		cancellationTo,
		reservationFrom,
		reservationTo,
		selectedStatuses,
		selectedVendors,
		selectedSections,
		isInAppSale,
		inAppSaleType,
		reservationType,
	]);

	const amountPaidTooltip = (
		<Box sx={{ maxWidth: 360 }}>
			<Typography variant="body2" color="inherit">
				El monto pagado por producto se estima repartiendo el descuento porcentual y el costo de servicio de la orden
				entre los ítems, según su cantidad.
			</Typography>
			<Typography variant="body2" color="inherit" sx={{ mt: 0.5, fontWeight: 'bold' }}>
				Si se usaron puntos o saldo de cortesía, no puede prorratearse cuánto se abonó por cada producto porque ese
				valor no es proporcional.
			</Typography>
		</Box>
	);

	return (
		<>
			{!!loading && <LinearProgress />}
			<Grid container spacing={2} alignItems="center">
				<Grid item xs={8}>
					<SearchToolbar
						onSearch={(value) => setSearch(value)}
						live={true}
						disabled={props.disabled}
						customAction={props.customAction}
					/>
				</Grid>
				<Grid item xs={8}>
					<DateFilters filters={filters} dispatch={dispatch} />
				</Grid>
				<Grid item xs={8}>
					<OtherFilters filters={filters} dispatch={dispatch} />
				</Grid>
				<Grid item xs={10}>
					<SoldSummaryBlock summary={summary} loading={summaryLoading} />
				</Grid>
				<Grid item container xs={4}>
					<Grid item xs={1}>
						<Tooltip title={t('common:refresh') || 'Actualizar'}>
							<IconButton onClick={() => refetch()}>
								<RefreshIcon />
							</IconButton>
						</Tooltip>
					</Grid>
					<Grid item xs={1}>
						<Tooltip title={t('common:downloadCsv') || 'Descargar CSV'}>
							<IconButton onClick={exportToCsv} disabled={exportingCsv}>
								{exportingCsv ? <CircularProgress size={24} /> : <DownloadIcon />}
							</IconButton>
						</Tooltip>
					</Grid>
				</Grid>
			</Grid>

			{!props.disabled && (
				<ResponsiveTable
					elements={displayedProducts}
					list={{
						primaryKey: '_id',
						avatar: <LocalGroceryStoreIcon />,
						renderListItemText: (soldProduct) => {
							return (
								<ListItemText
									onClick={() => false}
									primary={
										<>
											{`${soldProduct.title || ''} - ${soldProduct.vendor || ''}`}
											{soldProduct.isNfcCard && <MuteCardChip />}
										</>
									}
									secondary={`${t('common:date')}: ${soldProduct.createdAt || ''} | ${t('common:quantity')}: ${
										soldProduct.quantity || 0
									} | ${t('common:price')}: $${soldProduct.price || 0} | ${t('common:tableNumber')}: ${
										soldProduct.tableNumber || ''
									} `}
								/>
							);
						},
					}}
					table={{
						renderHead: () => (
							<TableHead>
								<TableRow>
									<TableCell align="left">Fecha Creación</TableCell>
									<TableCell align="left">Fecha Confirmación</TableCell>
									<TableCell align="left">Fecha Cancelación</TableCell>
									{inAppSaleType === 'reservation' && <TableCell align="left">Fecha Reserva</TableCell>}

									<TableCell align="left">Código Producto</TableCell>
									<TableCell align="left">Categoría</TableCell>
									<TableCell align="left">{t('common:description')}</TableCell>

									<TableCell align="left">{t('common:quantity')}</TableCell>
									<TableCell align="left">{t('common:price')}</TableCell>
									<TableCell align="left">
										<Tooltip title={amountPaidTooltip}>
											<Box
												component="span"
												sx={{ borderBottom: '1px dashed', borderColor: 'grey.400', cursor: 'help' }}
											>
												Monto Pagado
											</Box>
										</Tooltip>
									</TableCell>

									<TableCell align="left">Comprador</TableCell>
									<TableCell align="left">Usuario interno</TableCell>

									<TableCell align="left">Código Orden</TableCell>
									<TableCell align="left">Estado</TableCell>

									<TableCell align="left">
										<Tooltip
											title={
												<Box>
													<Typography variant="body2" color="inherit">
														El total de la orden es la suma del valor de todos los productos, sin descuentos ni costos.
													</Typography>
													<Typography variant="body2" color="inherit" sx={{ fontWeight: 'bold' }}>
														La tabla muestra valores por producto; verás este total repetido cuando una orden incluya
														varios ítems.
													</Typography>
												</Box>
											}
										>
											<Box
												component="span"
												sx={{ borderBottom: '1px dashed', borderColor: 'grey.400', cursor: 'help' }}
											>
												Total orden
											</Box>
										</Tooltip>
									</TableCell>
									<TableCell align="left">C/Servicio Orden</TableCell>
									<TableCell align="left">Descuento Orden</TableCell>
									{/* <TableCell align="left">
										<Tooltip
											title={
												<Box>
													<Typography variant="body2" color="inherit">
														El total pagado es el total de la orden después de aplicar descuentos y costos.
													</Typography>
													<Typography variant="body2" color="inherit" sx={{ fontWeight: 'bold' }}>
														La tabla muestra valores por producto; verás este total repetido cuando una orden incluya
														varios ítems.
													</Typography>
												</Box>
											}
										>
											<Box
												component="span"
												sx={{ borderBottom: '1px dashed', borderColor: 'grey.400', cursor: 'help' }}
											>
												Total Pagado
											</Box>
										</Tooltip>
									</TableCell> */}
									<TableCell align="left">Puntos Usados</TableCell>
									<TableCell align="left">Método de Pago</TableCell>
									<TableCell align="left">{t('common:location')}</TableCell>
								</TableRow>
							</TableHead>
						),
						renderRow: (soldProduct) => {
							const getSeller = () => {
								if (soldProduct.status === 'closed' && soldProduct.confirmedBy) {
									return soldProduct.confirmedBy.username;
								}
								if (soldProduct.status === 'cancelled' && soldProduct.cancelledBy) {
									return soldProduct.cancelledBy.username;
								}
								if (!soldProduct.isInAppSale && soldProduct.orderBy) {
									return soldProduct.orderBy.username;
								}
								return '-';
							};

							return (
								<TableRow tabIndex={-1} key={`${soldProduct.uniqueKey}`}>
									<TableCell>{moment(soldProduct.createdAt).format('DD/MM/yyyy HH:mm')}</TableCell>
									<TableCell>
										{soldProduct.confirmationDate
											? moment(soldProduct.confirmationDate).format('DD/MM/yyyy HH:mm')
											: '-'}
									</TableCell>
									<TableCell>
										{soldProduct.cancellationDate
											? moment(soldProduct.cancellationDate).format('DD/MM/yyyy HH:mm')
											: '-'}
									</TableCell>
									{inAppSaleType === 'reservation' && (
										<TableCell>
											{soldProduct.reservationDate
												? moment(soldProduct.reservationDate).format('DD/MM/yyyy HH:mm')
												: '-'}
										</TableCell>
									)}

									<TableCell>{soldProduct.code || '-'}</TableCell>
									<TableCell>{soldProduct.categories?.length ? soldProduct.categories.join(', ') : '-'}</TableCell>

									<TableCell>
										<p>
											{soldProduct.title}{' '}
											{!!soldProduct.tableNumber && <strong>(mesa {soldProduct.tableNumber})</strong>}
											<br />
											{!!soldProduct.mixer && <strong>{soldProduct.mixer}</strong>}
										</p>
									</TableCell>

									<TableCell>{soldProduct.quantity}</TableCell>

									<TableCell>{price(soldProduct.price)}</TableCell>
									<TableCell>
										{soldProduct.productAmountPaid !== null && soldProduct.productAmountPaid !== undefined ? (
											price(soldProduct.productAmountPaid)
										) : (
											<Tooltip title={amountPaidTooltip}>
												<Box
													component="span"
													sx={{ borderBottom: '1px dashed', borderColor: 'grey.400', cursor: 'help' }}
												>
													N/A
												</Box>
											</Tooltip>
										)}
									</TableCell>

									<TableCell>
										{soldProduct.orderTo?.username || soldProduct.vendor}
										{soldProduct.isNfcCard && <MuteCardChip />}
									</TableCell>
									<TableCell>{getSeller()}</TableCell>
									<TableCell>{soldProduct.orderCode || 'unknown'}</TableCell>
									<TableCell>{getStatusLabel(soldProduct.status)}</TableCell>
									<TableCell>{price(soldProduct.orderTotal ?? 0)}</TableCell>
									<TableCell>{price(soldProduct.saleFee ?? 0)}</TableCell>
									<TableCell>{price(soldProduct.saleDiscount ?? 0)}</TableCell>
									{/* <TableCell>{price(soldProduct.amountPaid || soldProduct.priceTotal)}</TableCell> */}
									<TableCell>{soldProduct.pointsUsed ?? 0}</TableCell>
									<TableCell>{getPaymentMethod(soldProduct.paymentMethod || '-')}</TableCell>
									<TableCell>{soldProduct?.sectionData?.title || '-'}</TableCell>
								</TableRow>
							);
						},
						pagination: props.disabled
							? null
							: {
									count: totalCount,
									page,
									rowsPerPage,
									rowsPerPageOptions: soldProductsRowsPerPageOptions,
									onPageChange: handleChangePage,
									onRowsPerPageChange: handleChangeRowsPerPage,
							  },
					}}
				/>
			)}
		</>
	);
}

function getPaymentMethod(paymentMethod: string | undefined) {
	switch (paymentMethod) {
		case 'wallet':
			return 'billetera';
		case 'userBalance':
			return 'cortesía';
		default:
			return paymentMethod;
	}
}
